import {
  BillingAddressRequestType,
  CheckoutAddressType,
  ShippingAddressRequestType,
  UpdateCheckoutRequestBodyType,
} from 'b2b-common/core/checkout/Checkout.types';

enum CHECKOUT_BLOCKER {
  address = 'address',
  licenseHolder = 'licenseHolder',
  payment = 'payment',
  shipping = 'shipping',
}

enum SHIPPING_METHOD {
  mail = 'mail',
  mailBulky = 'mailBulky',
  pickup = 'pickup',
  pickupWillisau = 'pickupWillisau',
  pickupRenens = 'pickupRenens',
  saturdayMail = 'saturdayMail',
  express = 'express',
  carrier = 'carrier',
  npu = 'npu',
  distribution = 'distribution',
  distributionMaegenwil = 'distributionMaegenwil',
  distributionOhringen = 'distributionOhringen',
  distributionWillisau = 'distributionWillisau'
}

enum DROP_SHIPPING_OPTION {
  alltron = 'alltron',
  industry = 'industry',
  anonymous = 'anonymous',
}

enum SALUTATION {
  male = 'Herr',
  female = 'Frau',
  company = 'Firma',
}

enum SHIPPING_OPTION {
  allInOne = 'allInOne',
  twoParts = 'twoParts',
  eachSeparately = 'eachSeparately',
}

enum ADDRESS_TYPE {
  new = 'new',
  preferred = 'preferred',
  billing = 'billing',
  saved = 'saved',
}

enum PAYMENT_TYPE {
  bill = 'bill',
  cash = 'cash',
  cashOnDelivery = 'cashOnDelivery',
  masterCard = 'masterCard',
  postFinanceCard = 'postfinanceCard',
  postFinanceEFinance = 'postfinanceEFinance',
  prepayment = 'prepayment',
  twint = 'twint',
  visa = 'visa',
  payPal = 'paypal',
  americanExpress = 'americanExpress',
  samsungPay = 'samsungpay',
}

enum PAYMENT_STATUS {
  canceled = 'canceled',
  failed = 'failed',
}

type AddresseeType = {
  company: string | null,
  contactPerson: string | null,
  firstname: string | null,
  lastname: string | null,
  salutation: string | null
}

const hasCustomerReference = (
  dropShippingOption: DROP_SHIPPING_OPTION | any,
): boolean => dropShippingOption === DROP_SHIPPING_OPTION.anonymous;

const isAddressCorrect = (address: CheckoutAddressType | Record<string, any>): boolean => !!(
  (address.name || (address.firstName && address.lastName))
  && address.street
  && address.postCode
  && address.city
);

const areAddressesEqual = (a: Record<string, any>, b: Record<string, any>): boolean => (a.street || '') === (b.street || '')
  && (a.streetAddition || '') === (b.streetAddition || '')
  && (a.contactPerson || '') === (b.contactPerson || '')
  && (a.name || '') === (b.name || '')
  && (a.city || '') === (b.city || '')
  && (a.postCode || '') === (b.postCode || '');

const getAddressType = (address: CheckoutAddressType, addresses: Record<string, any>): ADDRESS_TYPE => {
  switch (true) {
    case !isAddressCorrect(addresses.preferred):
      return ADDRESS_TYPE.new;

    case areAddressesEqual(address, addresses.preferred):
      return ADDRESS_TYPE.preferred;

    case areAddressesEqual(address, addresses.billing):
      return ADDRESS_TYPE.billing;

    case Array.isArray(addresses.saved)
      && addresses.saved.some((adr) => areAddressesEqual(address, adr)):
      return ADDRESS_TYPE.saved;

    default:
      return ADDRESS_TYPE.new;
  }
};

const comparePaymentMethods = (
  firstMethod: {
    order: number;
    type: string;
  },
  secondMethod: {
    order: number;
    type: string;
  },
): number => {
  const priorityMethods = [
    PAYMENT_TYPE.bill as string,
    PAYMENT_TYPE.masterCard as string,
    PAYMENT_TYPE.visa as string,
    PAYMENT_TYPE.postFinanceCard as string,
    PAYMENT_TYPE.postFinanceEFinance as string,
    PAYMENT_TYPE.cash as string,
    PAYMENT_TYPE.cashOnDelivery as string,
    PAYMENT_TYPE.prepayment as string,
  ];

  const firstMethodType = firstMethod.type;
  const secondMethodType = secondMethod.type;
  const haveFirstMethodPriority = priorityMethods.includes(firstMethodType);
  const haveSecondMethodPriority = priorityMethods.includes(secondMethodType);

  switch (true) {
    case haveFirstMethodPriority && haveSecondMethodPriority:
      return (
        priorityMethods.indexOf(firstMethodType)
        - priorityMethods.indexOf(secondMethodType)
      );

    case haveFirstMethodPriority:
      return -1;

    case haveSecondMethodPriority:
      return 1;

    default:
      return firstMethod.order - secondMethod.order;
  }
};

const isAddresseePrivate = (
  { salutation }: CheckoutAddressType,
): boolean => [SALUTATION.male, SALUTATION.female].includes(salutation as SALUTATION);

const mapBillingToRequest = (address: CheckoutAddressType): BillingAddressRequestType => {
  const isPrivate = isAddresseePrivate(address);
  const addressee: AddresseeType = isPrivate
    ? {
      company: null,
      contactPerson: null,
      firstname: address.firstName || null,
      lastname: address.lastName || null,
      salutation: address.salutation || null,
    }
    : {
      company: address.name,
      contactPerson: address.contactPerson,
      firstname: null,
      lastname: null,
      salutation: address.salutation || null,
    };
  return {
    ...addressee,
    city: address.city,
    street: address.street,
    streetaddition: address.streetAddition,
    zip: address.postCode,
  };
};

const mapShippingToRequest = (address: CheckoutAddressType): ShippingAddressRequestType => ({
  city: address.city,
  contactPerson: address.contactPerson,
  displayName: address.name,
  street: address.street,
  streetaddition: address.streetAddition,
  zip: address.postCode,
});

const isShippingMethodPickup = (shippingMethod: SHIPPING_METHOD): boolean => {
  const pickupShippingMethods = [
    SHIPPING_METHOD.pickup,
    SHIPPING_METHOD.pickupWillisau,
    SHIPPING_METHOD.pickupRenens,
  ];
  return pickupShippingMethods.includes(shippingMethod);
};

const prepareRequestData = (requestData: UpdateCheckoutRequestBodyType): UpdateCheckoutRequestBodyType => {
  isShippingMethodPickup(requestData.shipping.type)
  && delete requestData.shippingAddress;
  return requestData;
};

export {
  ADDRESS_TYPE,
  areAddressesEqual,
  CHECKOUT_BLOCKER,
  comparePaymentMethods,
  DROP_SHIPPING_OPTION,
  getAddressType,
  hasCustomerReference,
  isAddressCorrect,
  isAddresseePrivate,
  isShippingMethodPickup,
  mapBillingToRequest,
  mapShippingToRequest,
  PAYMENT_STATUS,
  PAYMENT_TYPE,
  prepareRequestData,
  SALUTATION,
  SHIPPING_METHOD,
  SHIPPING_OPTION,
};
